export type SalaryLocale = 'en' | 'fi' | 'sv';

const NUMBER_LOCALE: Record<SalaryLocale, string> = { en: 'en-US', fi: 'fi-FI', sv: 'sv-FI' };
const PER_MONTH: Record<SalaryLocale, string> = { en: '/month', fi: '/kk', sv: '/mån' };

function toSalaryLocale(locale: string): SalaryLocale {
  return locale === 'fi' || locale === 'sv' ? locale : 'en';
}

/**
 * Whole-euro amount in the locale's own style: "€2,416" (en), "2 416 €" (fi, sv).
 *
 * The one salary formatter for the location market header. It replaced three that disagreed
 * on the same number ("€3k" whole-k, "€2.7k" one-decimal-k and "€2,416/month"), which put
 * contradictory pay figures a few pixels apart on every city page, with English units on
 * the fi and sv pages.
 */
export function formatEuroAmount(value: number, locale: string): string {
  return new Intl.NumberFormat(NUMBER_LOCALE[toSalaryLocale(locale)], {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

/** "€2,416/month" (en), "2 416 €/kk" (fi), "2 416 €/mån" (sv). */
export function formatMonthlySalary(value: number, locale: string): string {
  return `${formatEuroAmount(value, locale)}${PER_MONTH[toSalaryLocale(locale)]}`;
}

/** "€2,061–€3,200/month" (en), "2 061–3 200 €/kk" (fi), "2 061–3 200 €/mån" (sv). */
export function formatMonthlySalaryRange(low: number, high: number, locale: string): string {
  const l = toSalaryLocale(locale);
  if (l === 'en') return `${formatEuroAmount(low, l)}–${formatEuroAmount(high, l)}${PER_MONTH[l]}`;
  const number = new Intl.NumberFormat(NUMBER_LOCALE[l], { maximumFractionDigits: 0 });
  return `${number.format(Math.round(low))}–${number.format(Math.round(high))} €${PER_MONTH[l]}`;
}
